import { Children, cloneElement, forwardRef, isValidElement, useId } from "react";
import { ChevronDown, Search } from "lucide-react";
import cn from "./cn";

// DESIGN.md › Components › Inputs / Fields. `data-ui` opts these controls out
// of the legacy global input restyling in operations-portal.css / business-ui.css.
const control = cn(
  "w-full rounded-control border border-default bg-surface text-body text-foreground",
  "placeholder:text-tertiary hover:border-strong",
  "focus:border-accent focus:outline-none focus:ring-1 focus:ring-focus",
  "aria-[invalid=true]:border-danger",
  "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-tertiary disabled:hover:border-default",
  "transition-colors duration-150"
);

export const Input = forwardRef(function Input({ className, type = "text", ...props }, ref) {
  return (
    <input ref={ref} type={type} data-ui="input" className={cn(control, "h-control px-3 max-md:h-touch", className)} {...props} />
  );
});

export const Textarea = forwardRef(function Textarea({ className, rows = 3, ...props }, ref) {
  return <textarea ref={ref} rows={rows} data-ui="input" className={cn(control, "min-h-20 px-3 py-2", className)} {...props} />;
});

/** Native select: right for simple filters and short fixed lists. */
export const Select = forwardRef(function Select({ className, children, ...props }, ref) {
  return (
    <span className="relative block">
      <select ref={ref} data-ui="input" className={cn(control, "h-control appearance-none pl-3 pr-8 max-md:h-touch", className)} {...props}>
        {children}
      </select>
      <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
    </span>
  );
});

/** Search box with its icon drawn by the component, not by global CSS. */
export const SearchInput = forwardRef(function SearchInput({ className, label = "Search", ...props }, ref) {
  return (
    <span className={cn("relative block", className)}>
      <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
      <input ref={ref} type="search" aria-label={label} data-ui="input" className={cn(control, "h-control pl-8 pr-3 max-md:h-touch")} {...props} />
    </span>
  );
});

/**
 * Field — label, one control, optional hint and one error message. The label
 * and messages are linked to the control (htmlFor, aria-describedby,
 * aria-invalid), so assistive technology reads them together.
 */
export default function Field({ label, hint, error, required = false, id: idProp, className, children }) {
  const autoId = useId();
  const id = idProp || `field-${autoId.replace(/:/g, "")}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const child = Children.only(children);
  const describedBy = [hintId, errorId, isValidElement(child) ? child.props["aria-describedby"] : null].filter(Boolean).join(" ");
  const controlElement = cloneElement(child, {
    id,
    required: required || child.props.required,
    "aria-describedby": describedBy || undefined,
    "aria-invalid": error ? true : child.props["aria-invalid"],
  });

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-label text-secondary">
        {label}
        {required ? <span aria-hidden="true" className="text-danger"> *</span> : null}
      </label>
      {controlElement}
      {hint ? <p id={hintId} className="text-meta text-tertiary">{hint}</p> : null}
      {error ? <p id={errorId} className="text-meta text-danger">{error}</p> : null}
    </div>
  );
}
